// Task 24584 — the history of one leave request, inside the edit modal.
//
// There is no field-level audit table for StaffLeave, so this is not a diff of
// what changed. It is the request's timeline built from what is actually
// recorded: when it was raised, when it was last touched, and every decision in
// the approval chain — who it went to, what they said, when, and any notes.
// That is the part people ask "what happened to this?" about.
//
// The decision list comes from GET /staffleave/data/{id}. Until Task 24584 that
// list came back empty every time (it filtered on a key that did not exist, and
// on pending-only), so this tab is the first thing to display it.

import React from "react";
import HandleDateTime, {
  HandleDate,
} from "../../Common/HandleDateTime";

const dash = "—";

const when = (value) => (value ? HandleDateTime(value) : dash);

// One decision row, read as a sentence. The flags are mutually exclusive in
// practice but checked in priority order so a row carrying more than one still
// renders as the strongest thing that happened to it.
const describeDecision = (decision) => {
  const who = decision?.Staff_FullName || "an approver";
  if (String(decision?.FlagApprove) === "1") {
    return {
      icon: "ri-check-line",
      tone: "text-success",
      title: `Approved by ${who}`,
      at: decision?.DecisionDateTime,
    };
  }
  if (String(decision?.FlagDecline) === "1") {
    return {
      icon: "ri-close-line",
      tone: "text-danger",
      title: `Declined by ${who}`,
      at: decision?.DecisionDateTime,
    };
  }
  if (String(decision?.FlagSkippedNoReply) === "1") {
    return {
      icon: "ri-time-line",
      tone: "text-warning",
      title: `Skipped — ${who} did not reply in time`,
      at: decision?.DecisionDateTime || decision?.ExpiryDateTime,
    };
  }
  if (String(decision?.FlagSkippedStaffOnHoliday) === "1") {
    return {
      icon: "ri-flight-takeoff-line",
      tone: "text-warning",
      title: `Skipped — ${who} was on leave`,
      at: decision?.DecisionDateTime || decision?.ExpiryDateTime,
    };
  }
  return {
    icon: "ri-hourglass-line",
    tone: "text-muted",
    title: `Waiting on ${who}`,
    at: null,
    pending: true,
  };
};

const LeaveRequestHistory = ({ leave }) => {
  if (!leave) {
    return (
      <p className="text-muted mb-0">
        Save the request first and its history will appear here.
      </p>
    );
  }

  const decisions = leave?.staffleavedecision_list || [];
  // Only worth showing when it differs from the creation stamp; otherwise it is
  // the same event reported twice.
  const edited =
    leave?.UpdatedDateTime &&
    String(leave.UpdatedDateTime) !== String(leave?.CreatedDateTime);

  return (
    <div>
      <dl className="row mb-3">
        <dt className="col-5 text-muted fw-normal">Request</dt>
        <dd className="col-7 mb-1">#{leave?.Id ?? dash}</dd>

        <dt className="col-5 text-muted fw-normal">Status</dt>
        <dd className="col-7 mb-1">
          {leave?.StaffLeaveStatus_Name || dash}
        </dd>

        <dt className="col-5 text-muted fw-normal">Dates</dt>
        <dd className="col-7 mb-1">
          {leave?.StartDateTime ? HandleDate(leave.StartDateTime) : dash}
          {" to "}
          {leave?.EndDateTime ? HandleDate(leave.EndDateTime) : dash}
        </dd>

        <dt className="col-5 text-muted fw-normal">Raised</dt>
        <dd className="col-7 mb-1">{when(leave?.CreatedDateTime)}</dd>

        {edited && (
          <>
            <dt className="col-5 text-muted fw-normal">Last changed</dt>
            <dd className="col-7 mb-1">{when(leave.UpdatedDateTime)}</dd>
          </>
        )}
      </dl>

      <h6 className="mb-2">Approval chain</h6>
      {decisions.length === 0 ? (
        <p className="text-muted mb-0">
          No approvers are recorded against this request.
        </p>
      ) : (
        <ul className="list-unstyled mb-0">
          {decisions.map((decision, index) => {
            const detail = describeDecision(decision);
            return (
              <li
                key={decision?.Id ?? index}
                className="d-flex gap-2 mb-2 pb-2 border-bottom"
              >
                <i className={`${detail.icon} ${detail.tone} mt-1`} />
                <div className="flex-grow-1">
                  <div className={`fw-semibold ${detail.tone}`}>
                    {detail.title}
                  </div>
                  <div className="text-muted small">
                    {detail.pending
                      ? decision?.ExpiryDateTime
                        ? `Expires ${when(decision.ExpiryDateTime)}`
                        : "No reply yet"
                      : when(detail.at)}
                  </div>
                  {decision?.Notes && (
                    <div className="small mt-1">{decision.Notes}</div>
                  )}
                  {decision?.NotesPrivate && (
                    <div className="small mt-1 text-muted fst-italic">
                      Private: {decision.NotesPrivate}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <p className="text-muted small mt-3 mb-0">
        Changes to the dates or the day count aren't individually logged — this
        shows when the request was raised, when it was last changed, and every
        approval decision made on it.
      </p>
    </div>
  );
};

export default LeaveRequestHistory;
